import { Component, Input, forwardRef } from '@angular/core';
import { Device, getDeviceIconIdentifier, ProductModelService, System } from 'ngx-core-services';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { MatDialogModule } from '@angular/material/dialog';
import { SearchComponent } from 'ngx-zcs-blocks';
import { ApplyPipeModule } from 'ngx-zcs-components';

import { SharedModule } from '../../shared.module';

const MAX_RESULTS = 5;

@Component({
  standalone: true,
  imports: [
    ApplyPipeModule,
    CommonModule,
    MatAutocompleteModule,
    MatIconModule,
    SharedModule,
    RouterModule,
    MatDialogModule,
    forwardRef(() => SearchComponent),
  ],
  selector: 'customer-search-system',
  templateUrl: './search-system.component.html',
  styleUrls: ['./search-system.component.scss'],
})
export class SearchSystemComponent {
  @Input() dialogMode = false;
  @Input() set systems(value: System[] | undefined | null) {
    this.systems$.next(value);
  }
  get systems(): System[] | undefined | null {
    return this.systems$.value;
  }
  readonly systems$ = new BehaviorSubject<System[] | undefined | null>(undefined);

  readonly query$ = new BehaviorSubject<string>('');
  @Input() set query(value: string) {
    this.query$.next(value);
  }
  get query(): string {
    return this.query$.value;
  }

  readonly filteredOptions$: Observable<{
    devices: { system: System; device: Device }[];
    systems: System[];
    showMore: boolean;
  }> = combineLatest([
    this.systems$,
    this.query$,
  ]).pipe(
    map(([systems, query]) => {
      query = query.toLowerCase();
      const systemsFiltered = systems?.filter((system) =>
        system.name.toLowerCase().includes(query) ||
        system.connextSn?.toLowerCase().includes(query) ||
        system.configuration.azzurroHubId?.toLowerCase().includes(query) ||
        system.configuration.connextId?.toLowerCase().includes(query) ||
        system.devices.some((device) => device.id.toLowerCase().includes(query)),
      );

      const devicesFiltered = systems?.map((system) =>
          system.devices
            .map((device) => ({ system, device }))
            .filter(({ device }) => {
              const product = new ProductModelService(device.id);
              return product.type?.name !== 'connext';
            })
            .filter(({ device }) => device.id.toLowerCase().includes(query)),
        )
        .reduce((acc, val) => acc.concat(val), []);

      return {
        systems: (systemsFiltered || []).slice(0, MAX_RESULTS),
        devices: (devicesFiltered || []).slice(0, MAX_RESULTS),
        showMore: (systemsFiltered?.length || 0) > MAX_RESULTS && !!query,
      };
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly filteredSystems$ = this.filteredOptions$.pipe(
    map(({ systems }) => systems),
  );

  readonly filteredDevices$ = this.filteredOptions$.pipe(
    map(({ devices }) => devices),
  );

  readonly showMore$ = this.filteredOptions$.pipe(
    map(({ showMore }) => showMore),
  );

  readonly getDeviceIconIdentifier = getDeviceIconIdentifier;
}
